import * as angular from 'angular';
import * as $ from 'jquery';
import * as _ from 'lodash';
import * as moment from 'moment';

import { LogbookEntry, Pattern, ComponentDefinition, RefreshClickData, IStringDateRange2, IStringDateRange, SaveLogbookDto } from '../../models';
import { IAuthentication, IAuthenticationService, IAuthorizationService, IDateHelperFormatService, ILogbookService, ILoggerService } from '../../services';
import { refreshClick } from '../../common/events';
import { Roles, severityLevels } from '../../common/constants';
import { toLogbookEntry, toLogbookEntryDto } from '../../common/helpers/logBookEntryConverter';
import { EditableLogbookEntry } from './EditableLogbookEntry';

class LogbookStateController implements angular.IComponentController {
    actualRange: IStringDateRange;
    categories: string[];
    criteria: Pattern<EditableLogbookEntry>[];
    editMode: boolean;
    editedEntries: LogbookEntry[];
    entries: EditableLogbookEntry[];
    filteredEntries: EditableLogbookEntry[];
    format: string;
    formController: angular.IFormController;
    initialRange: IStringDateRange2;
    isEditionAllowed: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedEntry: LogbookEntry;

    private _user: IAuthentication;
    private _pristine: LogbookEntry[];

    static $inject = [
        '$scope',
        '$stateParams',
        'logbookService',
        'authenticationService',
        'authorizationService',
        'dateHelperFormatService',
        'translatedLoggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$stateParams: angular.ui.IStateParamsService,
        private _logbookService: ILogbookService,
        private _authenticationService: IAuthenticationService,
        private _authorizationService: IAuthorizationService,
        private _dateHelperFormatService: IDateHelperFormatService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this._user = this._authenticationService.authentication;
        this.format = this._dateHelperFormatService.getDateFormat();

        this.criteria = [
            {
                label: 'Description',
                placeholder: 'Search pattern on description',
                property: 'c',
                isMatch: (entry, value) => value.test(entry.comment),
                model: 'c'
            },
            {
                label: 'Severity',
                placeholder: 'Search pattern on severity',
                property: 's',
                model: 's',
                isMatch: (entry, value) => {
                    const matching = severityLevels.filter(d => value.test(d.value));
                    return _.some(matching, m => m.key === entry.severity);
                }
            },
            {
                label: 'Category',
                placeholder: 'Search pattern on category',
                property: 'cat',
                model: 'cat',
                isMatch: (entry, value) => _.some(entry.labels, c => value.test(c))
            },
            {
                label: 'User',
                placeholder: 'Search pattern on user',
                property: 'u',
                model: 'u',
                isMatch: (entry, value) => value.test(entry.user)
            },
            {
                label: 'Group',
                placeholder: 'Search pattern on group',
                property: 'g',
                model: 'g',
                isMatch: (entry, value) => value.test(entry.group.absoluteName)
            },{
                label: 'Group type',
                placeholder: 'Search pattern on group type',
                property: 'gt',
                model: 'gt',
                isMatch: (entry, value) => value.test(entry.group.type)
            }
        ];

        if (this._$stateParams['beginDate'] && this._$stateParams['endDate']) {
            const begin = this._$stateParams['beginDate'];
            const end = this._$stateParams['endDate'];
            this.refresh(begin, end);
            this.initialRange = { beginDate: begin, endDate: end };
        } else {
            this.displayLastWeek();
        }

        this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
            const dates = !data
                ? { beginDate: this.actualRange.startDate, endDate: this.actualRange.endDate }
                : data;
            if (dates.beginDate && !dates.endDate) {
                dates.endDate = moment(dates.beginDate).add(7, 'd').toISOString();
            }
            this.refresh(dates.beginDate, dates.endDate, data.force);
            this.actualRange = { startDate: dates.beginDate, endDate: dates.endDate };
        });

        this.isEditionAllowed = this._authorizationService.canEdit();
    }

    private displayLastWeek() {
        const endMoment = moment().startOf('day');
        const beginMoment = endMoment.clone().subtract(7, 'd');
        const lastWeek = {
            beginDateTime: beginMoment.toISOString(),
            endDateTime: endMoment.toISOString()
        };
        this.refresh(lastWeek.beginDateTime, lastWeek.endDateTime);
        this.initialRange = {
            beginDate: lastWeek.beginDateTime as string,
            endDate: lastWeek.endDateTime as string
        };
    }

    private refresh(beginDate: string, endDate: string, force?: boolean) {
        this.loadingMessage = 'Loading logbook entries';
        this.isLoading = true;
        this._logbookService.getAllLabels().then(data => {
            this.categories = data.map(d => d.name);
        });
        return this._logbookService
            .getDtoBetweenDates(beginDate, endDate, force)
            .then(data => {
                this.entries = data
                    .map(d => {
                        const lbe = toLogbookEntry(d);
                        const canEdit = this.canEdit(lbe);
                        const canDelete = !d.hasData;
                        return Object.assign(lbe, { canEdit, canDelete });
                    });
                this.filteredEntries = angular.copy(this.entries);
                return this.entries;
            })
            .finally(() => this.isLoading = false);
    }

    tryDelete(entry: LogbookEntry): void {
        this.selectedEntry = entry;
        $('#confirmDelete').modal('show');
    }

    onDelete(entry: LogbookEntry): void {
        this.loadingMessage = 'Deleting logbook entry';
        this.isLoading = true;
        this._logbookService
            .deleteEntry(entry.id, false, true)
            .then(() => {
                this._loggerService.logSuccess('Successfully deleted entry.');
                return this.refresh(this.actualRange.startDate, this.actualRange.endDate, true);
            })
            .then(() => this._pristine = angular.copy(this.entries))
            .finally(() => this.isLoading = false);
    }

    private canEdit(entry: LogbookEntry): boolean {
        return (entry.user === this._user.userName
            || this._user.roles.indexOf(Roles.master) >= 0);
    }

    editTable(): void {
        this._pristine = angular.copy(this.entries);
        this.editMode = true;
    }

    cancel() {
        angular.copy(this._pristine, this.entries);
        if (this.formController) {
            this.formController.$setPristine();
        }
        this.editMode = false;
    }

    save() {
        this.loadingMessage = 'Saving logbook entries';
        this.isLoading = true;
        const toUpdate = this.editedEntries.map(d => {
            return {
                entry: toLogbookEntryDto(d, false),
                isAsync: true,
                values: []
            } as SaveLogbookDto;
        });
        this._logbookService.update(toUpdate)
            .then(data => {
                this._loggerService.logSuccess(`Successfully updated ${toUpdate.length} entries.`);
                return this.postSave();
            })
            .catch(() => this._loggerService.logError('Failed to update entries.'))
            .finally(() => this.isLoading = false);
    }

    deleteEntries(entries: LogbookEntry[]) {
        this.loadingMessage = 'Deleting logbook entries';
        this.isLoading = true;
        const ids = entries.map(d => d.id);
        this._logbookService.deleteEntries(ids)
            .then(() => {
                this._loggerService.logSuccess('Successfully deleted logbook entries.');
                return this.postSave();
            })
            .catch(() => {
                this._loggerService.logError('Failed to delete logbook entries.');
            })
            .finally(() => this.isLoading = false);
    }

    upload(file: File) {
        this.isLoading = true;
        this.loadingMessage = 'Uploading logbook entries';
        return this._logbookService.upload(file)
            .then(data => {
                this.refresh(this.actualRange.startDate, this.actualRange.endDate, true);
                return data;
            })
            .finally(() => this.isLoading = false);
    }

    private postSave() {
        if (this.formController) {
            this.formController.$setPristine();
        }
        this.editedEntries = [];
        this.editMode = false;
        return this.refresh(this.actualRange.startDate, this.actualRange.endDate, true);
    }
}

export const logbookState: ComponentDefinition = {
    componentId: 'wtLogbookState',
    controller: LogbookStateController,
    controllerAs: 'vm',
    template: require('./logbookState.component.html'),
    bindings: {
        isMobileMode: '<?'
    }
};